import { isValidRunningHours, type RunningHoursError } from '../running-hours.js';
import { type EquipmentNode, MAX_DAILY_HOURS } from './equipment.js';

/**
 * Çalışma saati sayaçları (plan M7 Task 2; ADR 0029 §5; K2, K4, K5, K6). `running-hours.ts`
 * yerinde kalır; buradaki kurallar onun [NR496 4.5.1] kontrollerini sayaç değişimi, birikmiş
 * toplam ve ekipman başına günlük üst sınırla genişletir. Saf modül: `serverNow` parametredir.
 */

/** Okuma türü: sayaç okuması, sayaç değişimi ya da sıfırlaması (K4). */
export const COUNTER_READING_KINDS = ['reading', 'meter_replaced', 'meter_reset'] as const;
export type CounterReadingKind = (typeof COUNTER_READING_KINDS)[number];

export interface CounterReading {
  kind: CounterReadingKind;
  /** Okuma zamanı, ISO-8601 UTC. */
  readAt: string;
  /** Sayacın gösterdiği değer; olay satırında yeni sayacın başlangıcı (K4 sözleşmesi). */
  totalHours: number;
  /** Olayda eski sayacın son değeri. */
  previousMeterFinal?: number | null;
  /** Olayda yeni sayacın başlangıcı. */
  meterStartHours?: number | null;
  /** Olayın gerekçesi. */
  reason?: string | null;
  /** İptal edilmiş satır (M7b `record_voids`): hiçbir hesaba girmez. */
  voided?: boolean;
}

export type CounterError =
  | RunningHoursError
  | 'INVALID_KIND'
  | 'INVALID_HOURS'
  | 'PREVIOUS_METER_FINAL_REQUIRED'
  | 'METER_START_REQUIRED'
  | 'METER_START_MISMATCH'
  | 'METER_CHANGE_REASON_REQUIRED';

/** Kullanıcının tek tek, açıkça onaylaması gereken durumlar [NR496 4.5.1c, 4.5.1d] (K5). */
export const COUNTER_CONFIRMATIONS = [
  'LOWER_THAN_PREVIOUS',
  'DAILY_AVERAGE_OVER_24H',
  'DAILY_AVERAGE_OVER_LIMIT',
] as const;
export type CounterConfirmation = (typeof COUNTER_CONFIRMATIONS)[number];

export interface CounterCheck {
  errors: CounterError[];
  confirmationsRequired: CounterConfirmation[];
  /** Sunucunun satıra yazacağı birikmiş toplam (K4); hatalı okumada `undefined`. */
  cumulativeHours: number | undefined;
}

export interface CounterCheckOptions {
  /** Ekipmanın günlük üst sınırı (1–24). 24'ten büyük değer 24 sayılır: kontrol kapanmaz. */
  maxDailyHours: number;
  /** Gemi sunucusunun saati, ISO-8601 UTC (mimari belge §6.11). */
  serverNow: string;
}

const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;
/** Kullanım ortalamasının penceresi (K5). */
export const USAGE_WINDOW_DAYS = 90;

/** Değerler en çok bir ondalıktır; toplamda kayan nokta artığı kalmaz. */
const round1 = (value: number) => Math.round(value * 10) / 10;

const isEvent = (r: CounterReading) => r.kind === 'meter_replaced' || r.kind === 'meter_reset';
const live = (history: readonly CounterReading[]) => history.filter((r) => !r.voided);
const at = (r: CounterReading) => Date.parse(r.readAt);

/** Olayın ofseti: eski sayacın son değeri ile yeni sayacın başlangıcı arasındaki fark. */
function offsetOf(event: CounterReading): number {
  return (event.previousMeterFinal ?? 0) - (event.meterStartHours ?? event.totalHours);
}

/**
 * Birikmiş toplam (K4): sayacın gösterdiği değer artı önceki sayaç olaylarının ofseti. Olayın
 * kendi birikmiş toplamı eski sayacın son değeridir. İptal edilmiş satırlar hesaba girmez. Örnek:
 * eski son 25.000, yeni başlangıç 0, okuma 150 → 25.150.
 */
export function cumulativeHours(
  reading: CounterReading,
  history: readonly CounterReading[],
): number {
  const time = at(reading);
  let offset = 0;
  for (const row of live(history)) {
    if (row === reading || !isEvent(row) || at(row) >= time) continue;
    offset += offsetOf(row);
  }
  if (isEvent(reading)) offset += offsetOf(reading);
  return round1(reading.totalHours + offset);
}

/** Önceki okuma: iptal edilmemiş, okuma zamanı en geç olan satır (olay satırı dahil). */
function previousOf(history: readonly CounterReading[]): CounterReading | undefined {
  return [...live(history)].sort((a, b) => at(a) - at(b)).at(-1);
}

/**
 * Bir okumanın ya da sayaç olayının [NR496 4.5.1] kontrolleri (K4, K5). Uyum kuralları ayar
 * değildir [NR496 1.4.2]: gelecek tarih ve negatif değer reddedilir; düşük okuma ve günlük
 * ortalamanın 24'ü aşması her zaman onay ister; ekipman sınırı yalnız daha sıkı denetler.
 *
 * - "Önceki okuma" iptal edilmemiş son satırdır; olay satırının değeri yeni sayacın başlangıcıdır.
 *   Değişimden sonraki okuma yeni başlangıçla karşılaştırılır; eski sayaca göre düşük olması onay
 *   istemez.
 * - Olayda eski sayacın son değeri son okumayla karşılaştırılır (düşükse onay) ve eski segmentin
 *   günlük ortalaması denetlenir.
 * - Ortalama birikmiş toplamla, UTC zaman farkıyla hesaplanır.
 */
export function checkCounterReading(
  reading: CounterReading,
  history: readonly CounterReading[],
  options: CounterCheckOptions,
): CounterCheck {
  const errors: CounterError[] = [];
  const confirmationsRequired: CounterConfirmation[] = [];
  const done = (): CounterCheck => ({
    errors,
    confirmationsRequired,
    cumulativeHours: errors.length === 0 ? cumulativeHours(reading, history) : undefined,
  });

  if (!(COUNTER_READING_KINDS as readonly string[]).includes(reading.kind)) {
    errors.push('INVALID_KIND');
    return done();
  }
  const event = isEvent(reading);
  const previous = previousOf(history);
  const time = at(reading);
  const now = Date.parse(options.serverNow);
  const previousAt = previous ? at(previous) : null;
  if (
    Number.isNaN(time) ||
    Number.isNaN(now) ||
    (previousAt !== null && Number.isNaN(previousAt))
  ) {
    errors.push('INVALID_DATE');
    return done();
  }

  const values: (number | null | undefined)[] = [reading.totalHours];
  if (event) values.push(reading.previousMeterFinal, reading.meterStartHours);
  if (values.some((v) => v !== null && v !== undefined && !isValidRunningHours(v))) {
    errors.push('INVALID_HOURS');
    return done();
  }

  if (time > now) errors.push('FUTURE_DATE');
  if (values.some((v) => typeof v === 'number' && v < 0)) errors.push('NEGATIVE_VALUE');
  if (event) {
    if (reading.previousMeterFinal === null || reading.previousMeterFinal === undefined) {
      errors.push('PREVIOUS_METER_FINAL_REQUIRED');
    }
    if (reading.meterStartHours === null || reading.meterStartHours === undefined) {
      errors.push('METER_START_REQUIRED');
    } else if (reading.totalHours !== reading.meterStartHours) {
      errors.push('METER_START_MISMATCH');
    }
    if (!reading.reason?.trim()) errors.push('METER_CHANGE_REASON_REQUIRED');
  }
  if (!previous || previousAt === null) return done();
  if (time <= previousAt) {
    errors.push('NOT_AFTER_PREVIOUS_READING');
    return done();
  }

  // Karşılaştırılan değer: okumada sayacın değeri, olayda eski sayacın son değeri
  const compared = event ? reading.previousMeterFinal : reading.totalHours;
  if (typeof compared !== 'number') return done();
  const delta = compared - previous.totalHours;
  const elapsedHours = (time - previousAt) / HOUR_MS;
  if (delta < 0) {
    confirmationsRequired.push('LOWER_THAN_PREVIOUS');
    return done();
  }
  // Ortalama > 24 saat/gün  <=>  artış > geçen süre (saat) [NR496 4.5.1c]
  if (delta > elapsedHours) confirmationsRequired.push('DAILY_AVERAGE_OVER_24H');
  // Ekipman sınırı yalnız daha sıkıdır; 24'ün üstündeki ayar kontrolü kapatmaz (K5) [NR496 1.4.2]
  const limit = Math.min(options.maxDailyHours, MAX_DAILY_HOURS);
  if (limit < MAX_DAILY_HOURS && delta * MAX_DAILY_HOURS > elapsedHours * limit) {
    confirmationsRequired.push('DAILY_AVERAGE_OVER_LIMIT');
  }
  return done();
}

/**
 * Kullanım ortalaması (K5): son 90 günün iptal edilmemiş okumalarından, birikmiş toplamla, günde
 * saat. İki okumadan azsa `undefined`. Zaman farkı UTC ile ölçülür (saat farkı değişse de).
 * Saat bazlı vadenin tahmini tarihi bununla hesaplanır; tahmin yalnız gösterimdir.
 */
export function averageDailyHours(
  history: readonly CounterReading[],
  asOf: string,
): number | undefined {
  const end = Date.parse(asOf);
  const start = end - USAGE_WINDOW_DAYS * DAY_MS;
  const window = live(history)
    .filter((r) => at(r) >= start && at(r) <= end)
    .sort((a, b) => at(a) - at(b));
  const first = window[0];
  const last = window.at(-1);
  if (!first || !last || first === last) return undefined;
  const days = (at(last) - at(first)) / DAY_MS;
  if (days <= 0) return undefined;
  return (cumulativeHours(last, history) - cumulativeHours(first, history)) / days;
}

/** "Son okumadan bu yana" girişi: arayüz sayaç değerini hesaplar, sunucuya sayaç değeri gider. */
export function totalFromDelta(previousTotal: number, sinceLast: number): number {
  return round1(previousTotal + sinceLast);
}

/**
 * Bileşenin sayacı (K6): kendisi ya da sayaçlı en yakın ata (ör. Injectors → Main Engine). Yoksa
 * `undefined`; saat bazlı aralık o zaman reddedilir (`COUNTER_REQUIRED`, M7b).
 */
export function counterEquipmentFor(
  tree: readonly EquipmentNode[],
  id: string,
): string | undefined {
  const byId = new Map(tree.map((n) => [n.id, n]));
  const seen = new Set<string>();
  let cursor = byId.get(id);
  while (cursor && !seen.has(cursor.id)) {
    if (cursor.hasCounter) return cursor.id;
    seen.add(cursor.id);
    cursor = cursor.parentId === null ? undefined : byId.get(cursor.parentId);
  }
  return undefined;
}

export interface NoonReportLike {
  id: string;
  reportedAt: string;
  data: unknown;
}

export interface CounterSuggestion {
  hours: number;
  reportId: string;
  reportedAt: string;
}

const record = (value: unknown): Record<string, unknown> | undefined =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;

const sameName = (a: string, b: string) =>
  a.trim().toLocaleLowerCase('en') === b.trim().toLocaleLowerCase('en');

/**
 * Noon Report önerisi (K2): ekipmanın eşlemesine göre (`main_engine` ya da `auxiliary:<ad>`) son
 * gönderilmiş raporun çalışma saati. Yalnız öneridir, hiçbir şey yazmaz: kullanıcı onaylarsa
 * normal yazma yolundan, aynı [NR496 4.5.1] onaylarıyla okuma olur (`source = 'noon_report'`).
 * ADR 0022 §5'teki "aynı transaction" kararının yerini alır.
 */
export function suggestFromNoonReport(
  report: NoonReportLike | null | undefined,
  source: string | null | undefined,
): CounterSuggestion | undefined {
  if (!report || !source) return undefined;
  const machinery = record(record(report.data)?.machinery);
  let hours: unknown;
  if (source === 'main_engine') {
    hours = record(machinery?.mainEngine)?.runningHours;
  } else if (source.startsWith('auxiliary:')) {
    const name = source.slice('auxiliary:'.length);
    const list = Array.isArray(machinery?.auxiliaries) ? machinery.auxiliaries : [];
    const found = list
      .map(record)
      .find((aux) => typeof aux?.name === 'string' && sameName(aux.name, name));
    hours = found?.runningHours;
  }
  if (typeof hours !== 'number' || !Number.isFinite(hours) || hours < 0) return undefined;
  return { hours: round1(hours), reportId: report.id, reportedAt: report.reportedAt };
}
